import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useVideoService from '../../app/services/hooks/useVideoService';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import { formatCLP, formatDate, isSafePaymentUrl } from '../Organization/types';
import { Video, formatDuration } from './types';

// El enlace firmado dura 15 minutos: se renueva antes para no cortar la reproducción
const REFRESH_MS = 12 * 60 * 1000;

const VideoDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const { showVideo, getStreamUrl, purchaseVideo, fetchingVideo, fetchingStream, fetchingPurchase } = useVideoService();

    const [video, setVideo] = useState<Video | null>(null);
    const [streamUrl, setStreamUrl] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const playerRef = useRef<HTMLVideoElement>(null);

    // Posición a retomar cuando el reproductor carga el enlace renovado
    const resumeRef = useRef<{ position: number; playing: boolean } | null>(null);

    const loadStream = (renew = false) => {
        getStreamUrl(Number(id), {
            onSuccess: (response) => {
                const player = playerRef.current;
                if (renew && player) {
                    resumeRef.current = { position: player.currentTime || 0, playing: !player.paused };
                }
                setStreamUrl(response.data.url);
            },
            // Si falla una renovación se mantiene el enlace actual (sigue sirviendo unos minutos)
            onError: () => {
                if (!renew) setStreamUrl(null);
            }
        }, renew);
    };

    const handleLoadedMetadata = () => {
        const resume = resumeRef.current;
        const player = playerRef.current;
        if (!resume || !player) return;
        resumeRef.current = null;
        if (resume.position) player.currentTime = resume.position;
        if (resume.playing) player.play().catch(() => undefined);
    };

    useEffect(() => {
        showVideo(Number(id), {
            onSuccess: (response) => {
                setVideo(response.data.video);
                if (response.data.video.can_play) loadStream();
            },
            onError: () => setNotFound(true)
        });
        const payment = new URLSearchParams(location.search).get('payment');
        if (payment === 'success') toast.success('¡Pago confirmado! Ya puedes ver el video.');
        if (payment === 'pending') toast.info('Tu pago aún no se confirma. Si ya pagaste, revisa en unos minutos.');
    }, [id]);

    useEffect(() => {
        if (!streamUrl) return;
        const timer = setInterval(() => loadStream(true), REFRESH_MS);
        return () => clearInterval(timer);
    }, [streamUrl]);

    const handlePurchase = () => {
        purchaseVideo(Number(id), {
            onSuccess: (response) => {
                if (!isSafePaymentUrl(response.data.payment_url)) {
                    toast.error('No se pudo abrir el pago. Intenta nuevamente.');
                    return;
                }
                toast.info(response.message);
                window.location.href = response.data.payment_url;
            },
            onError: (response) => toast.error(response.message)
        });
    };

    if (notFound) {
        return <DefaultCard><p className="text-center text-muted py-5 mb-0">No se encontró el video.</p></DefaultCard>;
    }
    if (!video) {
        return <div className="text-center py-5">{fetchingVideo && <div className="spinner-border text-primary" />}</div>;
    }

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Videos', path: '/intranet/videos' },
                    { label: video.title, isCurrent: true }
                ]}
                title={video.title}
                description={`${video.publisher}${video.duration_seconds ? ` · ${formatDuration(video.duration_seconds)}` : ''}${video.published_at ? ` · ${formatDate(video.published_at)}` : ''}`}
            />
            <div className="row">
                <div className="col-lg-8 mb-3">
                    <DefaultCard>
                        {video.can_play ? (
                            streamUrl ? (
                                <video
                                    ref={playerRef}
                                    src={streamUrl}
                                    controls
                                    controlsList="nodownload"
                                    onLoadedMetadata={handleLoadedMetadata}
                                    onContextMenu={(e) => e.preventDefault()}
                                    style={{ width: '100%', borderRadius: 8, background: '#000' }}
                                    data-testid="video-player"
                                />
                            ) : (
                                <div className="text-center py-5">{fetchingStream ? <div className="spinner-border text-primary" /> : 'No se pudo cargar el video.'}</div>
                            )
                        ) : (
                            <div className="bg-dark rounded d-flex flex-column align-items-center justify-content-center text-white text-center p-4"
                                 style={{ aspectRatio: '16 / 9' }}>
                                <i className="fas fa-lock fa-3x mb-3" />
                                <h5>Video de pago</h5>
                                <p className="mb-3">Desbloquéalo con un pago único de {formatCLP(video.price_with_tax)} (IVA incluido).</p>
                                <ButtonGeneral onClick={handlePurchase} title="Comprar con Flow" loading={fetchingPurchase} icon="fa-shopping-cart" />
                            </div>
                        )}
                    </DefaultCard>
                </div>
                <div className="col-lg-4 mb-3">
                    <DefaultCard>
                        <h6 className="text-muted text-uppercase small">Sobre este video</h6>
                        <p style={{ whiteSpace: 'pre-line' }}>{video.description || 'Sin descripción.'}</p>
                        <ul className="list-unstyled small text-muted mb-0">
                            <li><i className="fas fa-building me-2" />{video.publisher}</li>
                            <li><i className="fas fa-tag me-2" />{video.is_free ? 'Gratuito' : video.purchased ? 'Comprado' : formatCLP(video.price_with_tax)}</li>
                            {video.can_manage && <li><i className="fas fa-eye me-2" />{video.views_count} reproducciones · {video.status_display}</li>}
                        </ul>
                    </DefaultCard>
                </div>
            </div>
        </div>
    );
};

export default VideoDetail;
